"use client";

import { Loader2 } from "lucide-react";
import { describeToolExecuting } from "@/lib/agent/tool-display";

/**
 * 实时进度指示器，映射 PageAgent activity 事件为中文进度文案。
 * activity 事件类型：thinking | executing | executed | retrying | error。
 */
export interface AgentActivity {
  type: "thinking" | "executing" | "executed" | "retrying" | "error";
  tool?: string;
  input?: Record<string, unknown> | null;
  output?: string;
  message?: string;
  attempt?: number;
  maxAttempts?: number;
}

function describe(activity: AgentActivity): string {
  switch (activity.type) {
    case "thinking":
      return "正在思考…";
    case "executing":
      // 产品化文案：把原始工具名 + 入参转成自然中文（如「正在统计关键词「C罗」的热搜次数…」）
      return describeToolExecuting(activity.tool, activity.input);
    case "executed":
      return "已获取结果，正在整理…";
    case "retrying":
      return `正在重试（${activity.attempt ?? 0}/${activity.maxAttempts ?? 0}）…`;
    case "error":
      return activity.message ? `出错了：${activity.message}` : "出错了";
    default:
      return "正在处理…";
  }
}

export function AgentActivityView({ activity }: { activity: AgentActivity | null }) {
  if (!activity) return null;
  const isError = activity.type === "error";
  return (
    <div
      className="flex items-center gap-2 rounded-xl border border-sky-100 bg-sky-50/60 px-3 py-2 text-sm text-sky-700 animate-fade-in dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-300"
      aria-live="polite"
    >
      {isError ? (
        <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" />
      ) : (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      )}
      <span className="truncate">{describe(activity)}</span>
    </div>
  );
}
